/**
 * layout - the Drafting detail page's binding of the shared detail-page
 * surface.
 *
 * THE CANVAS IS THE DOCUMENT. Everything a draft page shows that is not the
 * document itself used to be crammed into the header row or thrown over the
 * page as a drawer:
 *
 *   versions  a `w-80 bg-white border-s` column tiled INSIDE the content flex
 *             row, with its own header and close button, toggled by a bespoke
 *             "Version history" pill in the header;
 *   activity  a hand-rolled `ActivityDrawer` over the whole page, reachable
 *             only from a menu item that flipped `showAuditLog`;
 *   access    the matter linkage badges (`DraftingAttachments`), inline in
 *             the header between the status badge and the View/Edit toggle,
 *             where three linked matters pushed the toggle off a laptop.
 *
 * All three are PANES now: they dock, stack, reorder, resize, and are
 * remembered per user - and the header is left with the breadcrumb, the
 * status badge and the actions menu.
 *
 * WHY ACCESS OPENS BY DEFAULT. It is the only one of the three that was on
 * screen before, as the header's matter badges. A returning user should not
 * have to discover a rail to find out which matter a draft belongs to, so the
 * pane set starts where the fixed layout ended; closing it is remembered.
 *
 * WHY ENTITY SCOPE. A draft is a document someone comes back to - the one
 * being negotiated against a matter wants its linkage open, the one being
 * regenerated wants its versions open. One shared key would make every
 * document inherit the arrangement of whichever was opened last.
 *
 * NO LEGACY KEYS. This surface is new; nothing was ever written for it.
 *
 * NO DETAIL PANE, NO READER PANE. The canvas IS the reader - the document
 * body, or a version of it being previewed - and nothing on this page selects
 * a row into a shared editor. So the page binds the selection-free hook and
 * declares neither, per the "declare only the panes the page produces" rule.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';
import { DRAFTING_SHARING_ENABLED } from '../draftingConfig';
import { isAiUsageEnabled } from '../../../config/featureFlags';

export type DraftingPanelId =
  | 'access'
  | 'versions'
  | 'activity'
  | 'people'
  | 'aiUsage';

/**
 * The canvas's "tabs" are the document's two READING MODES.
 *
 * They were a hand-rolled segmented control in the header
 * (`bg-gray-900 text-white` on the active half, which is the one treatment
 * the brand tokens have no name for). The bar the surface already draws is
 * the same control done once - and it is where the pane rail lives, so the
 * two things that change what you are looking at share a row.
 */
export type DraftingTab = 'view' | 'edit';

export const DRAFTING_TABS: DraftingTab[] = ['view', 'edit'];

export type DraftingPanelSlot = GenericSlot<DraftingPanelId>;
export type DraftingPanelLayout = GenericLayout<DraftingPanelId>;

/**
 * SHARING IS A FOURTH PANE, NOT A REUSE OF `access`.
 *
 * `access` is the MATTER LINKAGE and stays exactly that - the draft↔matter
 * relationship is being designed separately, so nothing here touches it.
 * Collaborator access is its own `people` pane, declared only while
 * `DRAFTING_SHARING_ENABLED` is on. With the flag off the surface is
 * byte-for-byte the three panes it has always had.
 */
/* AI usage is dropped from the surface where the feature is not released —
   no slot, no rail pill, no persisted visibility, so a pane can never open
   onto it. See `config/featureFlags.isAiUsageEnabled`. */
export const DRAFTING_PANEL_IDS: DraftingPanelId[] = [
  'access',
  'versions',
  'activity',
  ...(DRAFTING_SHARING_ENABLED ? (['people'] as DraftingPanelId[]) : []),
  ...(isAiUsageEnabled() ? (['aiUsage'] as DraftingPanelId[]) : []),
];

/**
 * ONE CEILING FOR THE WHOLE DOCK - and a wide one.
 *
 * Every pane on this page docks right, and `panelLayout.dockWidth` clamps a
 * dock's shared width to the TIGHTEST max among the panes stacked in it. So
 * `activity`'s 760 never applied: `access` and `versions` capped the whole
 * column at 640 and the drag simply stopped - on a 27" display, with a third
 * of the window empty beside a document that is set to a fixed measure and
 * cannot use the space either. The panes read as broken because nothing moved.
 *
 * A single shared ceiling is what makes the dock resizable at all, and 900 is
 * the width at which an activity row's badge, subject and actor stop wrapping.
 * The canvas keeps Word's measure at any window size, so the space a wide
 * screen adds goes to the panes - the only things on this page that can use it.
 */
const PANE_MAX = 900;

const DRAFTING_PANES: Record<DraftingPanelId, DetailPaneConfig> = {
  /**
   * The matter linkage - which matters this document is attached to, and the
   * way to attach another. There is no backend collaborator model for a
   * drafted document yet, so linkage IS this page's access surface.
   */
  access: {
    label: 'Linked matters',
    bounds: { min: 240, max: PANE_MAX },
    dock: 'right', index: 0, width: 300, visible: true, rail: true,
  },
  versions: {
    label: 'Version history',
    bounds: { min: 280, max: PANE_MAX },
    dock: 'right', index: 1, width: 340, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: PANE_MAX },
    dock: 'right', index: 2, width: 380, visible: false, rail: true,
  },
  /** Who this document is shared with - see `../draftingConfig`. */
  people: {
    label: 'Access',
    bounds: { min: 240, max: PANE_MAX },
    dock: 'right', index: 3, width: 320, visible: false, rail: true,
  },
  /*
    AI USAGE - what generating and regenerating this document has cost.

    It belongs next to Version history rather than anywhere near the actions
    menu: a redraft is both a new version AND a new charge, and the two lists
    are read against each other when someone asks why a document was
    expensive. Its floor is 300 rather than the versions pane's 280 - a run
    row carries a face, a time and a cost on one baseline.
  */
  aiUsage: {
    label: 'AI usage',
    bounds: { min: 300, max: PANE_MAX },
    dock: 'right', index: 4, width: 360, visible: false, rail: true,
  },
};

export const DRAFTING_SURFACE = defineDetailSurface<DraftingPanelId, DraftingTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `drafting-surface`, etc.
  name: 'drafting',
  scope: 'entity',
  ids: DRAFTING_PANEL_IDS,
  panes: DRAFTING_PANES,
  tabs: DRAFTING_TABS,
});

/* ── derived, for call sites and tests that want the plain records ── */

export const DRAFTING_PANEL_LABELS: Record<DraftingPanelId, string> = DRAFTING_SURFACE.labels;
export const DRAFTING_PANEL_BOUNDS: Record<DraftingPanelId, PanelBounds> = DRAFTING_SURFACE.bounds;
export const DRAFTING_DEFAULT_LAYOUT: DraftingPanelLayout = DRAFTING_SURFACE.defaults;
